import React, { useMemo } from "react";
import { useWizard } from "@/context/WizardContext";
import { TextInput, SelectInput, SectionHeading, Grid, FieldLabel } from "@/components/wizard/FormKit";
import NarrativeField from "@/components/wizard/NarrativeField";
import {
  PresetProfileBar,
  ImmunizationsSection,
  PrognosisSection,
  CaregiverCapabilitySection,
  OxygenSafetySection,
  SafetyAbuseSection,
  FinancialSection,
  TeachingSection,
  MAHC10Section,
  GGScoresSection,
  PriorFunctioningSection,
  SensorySection,
  LifestyleSection,
  WeeklyBehaviorsSection,
  ContinenceDetailSection,
  IadlDetailSection,
  TransportationSection,
  HousekeepingSection,
  ActivitiesSection,
  MedicationManagementSection,
  NotesAndSignatureSection,
} from "./Step7NSA.extensions";

// ------- Shared select options -----------------------------------------------
// All <SelectInput options={...}> arrays are pinned at module scope so each
// render shares the same array reference. Inline options={[...]} would
// allocate fresh refs every render and force the SelectInput to memo-bust.
const YN = ["Yes", "No"];
const YNU = ["Yes", "No", "Unknown"];
const SCALE_0_4 = [
  { value: "0", label: "0 — Never" },
  { value: "1", label: "1 — Rarely" },
  { value: "2", label: "2 — Sometimes" },
  { value: "3", label: "3 — Quite Frequently" },
  { value: "4", label: "4 — Nearly Always" },
];

// Diet/nutrition (Step 7 — Nutritional)
const DIET_TYPES = ["Regular", "Diabetic (carb-consistent)", "Low-sodium", "Renal", "Cardiac", "Mechanical soft", "Puree", "Thickened liquids", "NPO / tube feeding"];
const APPETITE_LEVELS = ["Good", "Fair", "Poor"];
const INTAKE_BUCKETS = [">75% of meals", "50–75%", "25–50%", "<25%"];
const FEEDING_TUBES = ["None", "G-tube", "J-tube", "NG tube"];
const MUST_SCORES = [
  { value: "0", label: "0 — Low risk" },
  { value: "1", label: "1 — Medium risk" },
  { value: "2", label: "≥ 2 — High risk" },
];

// Pain (Step 7 — Pain)
const PAIN_QUALITIES = ["Sharp", "Dull", "Burning", "Aching", "Throbbing", "Stabbing", "Radiating"];
const PAIN_TIMING = ["Constant", "Intermittent", "Activity-related", "Nighttime"];

// Skin (Step 7 — Skin)
const SKIN_TURGOR = ["Good", "Fair", "Poor"];
const SKIN_COLORS = ["WNL", "Pale", "Flushed", "Jaundiced", "Cyanotic"];

// Continence (Step 7)
const URINARY_PATTERNS = ["Continent", "Occasional incontinence", "Frequent incontinence", "Fully incontinent", "Indwelling catheter", "Suprapubic catheter", "Urostomy"];
const BOWEL_PATTERNS = ["Regular", "Constipation", "Diarrhea", "Incontinent", "Colostomy", "Ileostomy"];

// Mood / mental health (Step 7)
const MOOD_TYPES = ["Euthymic", "Anxious", "Depressed", "Labile", "Irritable", "Flat"];
const SUICIDAL_LEVELS = ["None", "Passive", "Active (refer urgently)"];

const BRADEN_OPTIONS = {
  sensory_perception: [
    { value: "1", label: "1 — Completely limited" },
    { value: "2", label: "2 — Very limited" },
    { value: "3", label: "3 — Slightly limited" },
    { value: "4", label: "4 — No impairment" },
  ],
  moisture: [
    { value: "1", label: "1 — Constantly moist" },
    { value: "2", label: "2 — Very moist" },
    { value: "3", label: "3 — Occasionally moist" },
    { value: "4", label: "4 — Rarely moist" },
  ],
  activity: [
    { value: "1", label: "1 — Bedfast" },
    { value: "2", label: "2 — Chairfast" },
    { value: "3", label: "3 — Walks occasionally" },
    { value: "4", label: "4 — Walks frequently" },
  ],
  mobility: [
    { value: "1", label: "1 — Completely immobile" },
    { value: "2", label: "2 — Very limited" },
    { value: "3", label: "3 — Slightly limited" },
    { value: "4", label: "4 — No limitation" },
  ],
  nutrition: [
    { value: "1", label: "1 — Very poor" },
    { value: "2", label: "2 — Probably inadequate" },
    { value: "3", label: "3 — Adequate" },
    { value: "4", label: "4 — Excellent" },
  ],
  friction_shear: [
    { value: "1", label: "1 — Problem" },
    { value: "2", label: "2 — Potential problem" },
    { value: "3", label: "3 — No apparent problem" },
  ],
};

function bradenRisk(total) {
  const t = parseInt(total, 10);
  if (!t) return "";
  if (t >= 19) return "No Risk";
  if (t >= 15) return "Mild Risk";
  if (t >= 13) return "Moderate Risk";
  if (t >= 10) return "High Risk";
  return "Very High Risk";
}

const MORSE_OPTIONS = {
  history_of_falls: [{ value: "0", label: "0 — No" }, { value: "25", label: "25 — Yes" }],
  secondary_diagnosis: [{ value: "0", label: "0 — No" }, { value: "15", label: "15 — Yes" }],
  ambulatory_aid: [
    { value: "0", label: "0 — None / bed rest / wheelchair / nurse" },
    { value: "15", label: "15 — Crutches / cane / walker" },
    { value: "30", label: "30 — Furniture" },
  ],
  iv_therapy: [{ value: "0", label: "0 — No" }, { value: "20", label: "20 — Yes (IV or Heparin lock)" }],
  gait: [
    { value: "0", label: "0 — Normal / bed rest / immobile" },
    { value: "10", label: "10 — Weak" },
    { value: "20", label: "20 — Impaired" },
  ],
  mental_status: [
    { value: "0", label: "0 — Oriented to own ability" },
    { value: "15", label: "15 — Forgets limitations" },
  ],
};

function morseRisk(total) {
  const t = parseInt(total, 10);
  if (isNaN(t)) return "";
  if (t >= 45) return "High Risk";
  if (t >= 25) return "Low Risk";
  return "No Risk";
}

// PHQ-2 (Patient Health Questionnaire-2) — 2 items × 0–3, total 0–6, cutoff 3.
const PHQ2_NOTE = "PHQ-2: total ≥ 3 warrants full PHQ-9 follow-up and referral per agency policy.";

// ------- Sub-sections --------------------------------------------------------
function SectionWrap({ eyebrow, title, description, children }) {
  return (
    <section className="card-surface fade-up">
      <SectionHeading eyebrow={eyebrow} title={title} description={description} />
      {children}
    </section>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>;
}

// ------- Main component ------------------------------------------------------
export default function Step7NSA() {
  const { data, update } = useWizard();
  const a = data.assessment || {};

  const setPath = (section, patch) => {
    update("assessment", { [section]: { ...(a[section] || {}), ...patch } });
  };

  // Computed Braden total + risk
  const bradenTotal = useMemo(() => {
    const b = a.braden || {};
    const keys = ["sensory_perception", "moisture", "activity", "mobility", "nutrition", "friction_shear"];
    const sum = keys.reduce((acc, k) => acc + (parseInt(b[k], 10) || 0), 0);
    return sum > 0 ? String(sum) : "";
  }, [a.braden]);

  // Computed Morse total
  const morseTotal = useMemo(() => {
    const m = a.morse || {};
    const keys = ["history_of_falls", "secondary_diagnosis", "ambulatory_aid", "iv_therapy", "gait", "mental_status"];
    const sum = keys.reduce((acc, k) => acc + (parseInt(m[k], 10) || 0), 0);
    return sum > 0 ? String(sum) : "";
  }, [a.morse]);

  // Computed caregiver Zarit-lite total
  const zaritTotal = useMemo(() => {
    const c = a.caregiver || {};
    const keys = ["z1_too_much", "z2_no_time_self", "z3_stressed_combining", "z4_strained_caregiving", "z5_burden"];
    const sum = keys.reduce((acc, k) => acc + (parseInt(c[k], 10) || 0), 0);
    return sum > 0 ? String(sum) : "";
  }, [a.caregiver]);

  // BMI computation
  const bmi = useMemo(() => {
    const h = parseFloat(a.anthro?.height_in);
    const w = parseFloat(a.anthro?.weight_lb);
    if (!h || !w) return "";
    const b = (w * 703) / (h * h);
    return b.toFixed(1);
  }, [a.anthro]);

  return (
    <div className="space-y-10">
      {/* --- Preset profile bar (optional one-tap defaults) ----------------- */}
      <PresetProfileBar />

      {/* --- Vitals --------------------------------------------------------- */}
      <SectionWrap eyebrow="Step 7 · Vitals" title="Vital Signs (most-recent baseline)"
        description="Record a recent set of vitals. These become the POC baseline for PCW monitoring thresholds.">
        <Row>
          <TextInput label="BP Systolic" placeholder="mmHg" value={a.vitals?.bp_systolic}
            onChange={(v) => setPath("vitals", { bp_systolic: v })} testid="nsa-vitals-bp-sys" />
          <TextInput label="BP Diastolic" placeholder="mmHg" value={a.vitals?.bp_diastolic}
            onChange={(v) => setPath("vitals", { bp_diastolic: v })} testid="nsa-vitals-bp-dia" />
          <TextInput label="Heart Rate" placeholder="bpm" value={a.vitals?.hr}
            onChange={(v) => setPath("vitals", { hr: v })} testid="nsa-vitals-hr" />
          <TextInput label="Resp Rate" placeholder="/min" value={a.vitals?.rr}
            onChange={(v) => setPath("vitals", { rr: v })} testid="nsa-vitals-rr" />
          <TextInput label="Temperature" placeholder="°F" value={a.vitals?.temp}
            onChange={(v) => setPath("vitals", { temp: v })} testid="nsa-vitals-temp" />
          <TextInput label="SpO₂" placeholder="%" value={a.vitals?.spo2}
            onChange={(v) => setPath("vitals", { spo2: v })} testid="nsa-vitals-spo2" />
          <TextInput label="Blood Glucose (if applicable)" placeholder="mg/dL" value={a.vitals?.blood_glucose}
            onChange={(v) => setPath("vitals", { blood_glucose: v })} testid="nsa-vitals-bg" />
          <TextInput label="Date" type="date" value={a.vitals?.vitals_date}
            onChange={(v) => setPath("vitals", { vitals_date: v })} testid="nsa-vitals-date" />
          <TextInput label="Time" type="time" value={a.vitals?.vitals_time}
            onChange={(v) => setPath("vitals", { vitals_time: v })} testid="nsa-vitals-time" />
        </Row>
        <div className="mt-4">
          <NarrativeField label="Baseline notes" testid="nsa-vitals-notes"
            fieldKey="assessment.vitals.baseline_notes"
            fieldPurpose="Baseline vital sign observations including typical range, POC monitoring parameters, and escalation triggers."
            value={a.vitals?.baseline_notes} onChange={(v) => setPath("vitals", { baseline_notes: v })} />
        </div>
      </SectionWrap>

      {/* --- Anthropometrics + Nutrition ----------------------------------- */}
      <SectionWrap eyebrow="Step 7 · Nutrition" title="Anthropometrics & Nutritional Assessment"
        description="Height / weight → BMI. Diet type and intake trend.">
        <Row>
          <TextInput label="Height (in)" value={a.anthro?.height_in}
            onChange={(v) => setPath("anthro", { height_in: v })} testid="nsa-anthro-height" />
          <TextInput label="Weight (lb)" value={a.anthro?.weight_lb}
            onChange={(v) => setPath("anthro", { weight_lb: v })} testid="nsa-anthro-weight" />
          <TextInput label="BMI (auto)" value={bmi} onChange={() => {}} testid="nsa-anthro-bmi" />
          <TextInput label="Recent weight loss (lb)" value={a.anthro?.recent_weight_loss_lb}
            onChange={(v) => setPath("anthro", { recent_weight_loss_lb: v })} testid="nsa-anthro-loss-lb" />
          <TextInput label="Over what period (months)" value={a.anthro?.recent_weight_loss_period}
            onChange={(v) => setPath("anthro", { recent_weight_loss_period: v })} testid="nsa-anthro-loss-period" />
        </Row>
        <div className="mt-4">
          <Row>
            <SelectInput label="Diet" value={a.nutrition?.diet}
              onChange={(v) => setPath("nutrition", { diet: v })}
              options={DIET_TYPES}
              testid="nsa-nut-diet" />
            <SelectInput label="Appetite" value={a.nutrition?.appetite}
              onChange={(v) => setPath("nutrition", { appetite: v })}
              options={APPETITE_LEVELS} testid="nsa-nut-appetite" />
            <SelectInput label="Food intake (last 7 days)" value={a.nutrition?.intake}
              onChange={(v) => setPath("nutrition", { intake: v })}
              options={INTAKE_BUCKETS} testid="nsa-nut-intake" />
            <TextInput label="Fluid intake (cups/day)" value={a.nutrition?.fluid_intake}
              onChange={(v) => setPath("nutrition", { fluid_intake: v })} testid="nsa-nut-fluid" />
            <SelectInput label="Swallowing issues" value={a.nutrition?.swallowing_issues}
              onChange={(v) => setPath("nutrition", { swallowing_issues: v })}
              options={YN} testid="nsa-nut-swallow" />
            <SelectInput label="Feeding tube" value={a.nutrition?.feeding_tube}
              onChange={(v) => setPath("nutrition", { feeding_tube: v })}
              options={FEEDING_TUBES} testid="nsa-nut-tube" />
            <SelectInput label="MUST score" value={a.nutrition?.must_score}
              onChange={(v) => setPath("nutrition", { must_score: v })}
              options={MUST_SCORES} testid="nsa-nut-must" />
          </Row>
          <div className="mt-3">
            <NarrativeField label="Nutritional notes" testid="nsa-nut-notes"
              fieldKey="assessment.nutrition.notes"
              fieldPurpose="Nutritional observations — swallowing, choking risk, dietary adherence, referrals to dietitian."
              value={a.nutrition?.notes} onChange={(v) => setPath("nutrition", { notes: v })} />
          </div>
        </div>
      </SectionWrap>

      {/* --- Prognosis & ER triggers --------------------------------------- */}
      <PrognosisSection />

      {/* --- Immunizations ------------------------------------------------- */}
      <ImmunizationsSection />

      {/* --- Pain (PQRST) --------------------------------------------------- */}
      <SectionWrap eyebrow="Step 7 · Pain" title="Pain Assessment (PQRST)"
        description="Numerical 0–10 plus Provocation / Quality / Region / Severity / Timing.">
        <Row>
          <SelectInput label="Pain present?" value={a.pain?.present}
            onChange={(v) => setPath("pain", { present: v })} options={YN} testid="nsa-pain-yn" />
          <SelectInput label="Severity (0–10)" value={a.pain?.severity}
            onChange={(v) => setPath("pain", { severity: v })}
            options={Array.from({ length: 11 }, (_, i) => ({ value: String(i), label: `${i}` }))}
            testid="nsa-pain-severity" />
          <TextInput label="Location / Region" value={a.pain?.location}
            onChange={(v) => setPath("pain", { location: v })} testid="nsa-pain-location" />
          <SelectInput label="Quality" value={a.pain?.quality}
            onChange={(v) => setPath("pain", { quality: v })}
            options={PAIN_QUALITIES}
            testid="nsa-pain-quality" />
          <SelectInput label="Timing" value={a.pain?.timing}
            onChange={(v) => setPath("pain", { timing: v })}
            options={PAIN_TIMING} testid="nsa-pain-timing" />
          <TextInput label="Provocation (what worsens)" value={a.pain?.provocation}
            onChange={(v) => setPath("pain", { provocation: v })} testid="nsa-pain-prov" />
          <TextInput label="Relief (what helps)" value={a.pain?.relief}
            onChange={(v) => setPath("pain", { relief: v })} testid="nsa-pain-relief" />
          <SelectInput label="Interferes with ADLs?" value={a.pain?.interferes_adls}
            onChange={(v) => setPath("pain", { interferes_adls: v })} options={YN} testid="nsa-pain-adl" />
        </Row>
      </SectionWrap>

      {/* --- Braden Scale --------------------------------------------------- */}
      <SectionWrap eyebrow="Step 7 · Skin Integrity Risk" title="Braden Scale (pressure injury risk)"
        description="6 subscales. Total of 6 items produces a risk category. Target score ≥ 18 for low-risk.">
        <Row>
          {Object.entries(BRADEN_OPTIONS).map(([k, opts]) => (
            <SelectInput key={k}
              label={k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              value={a.braden?.[k]} onChange={(v) => setPath("braden", { [k]: v })}
              options={opts} testid={`nsa-braden-${k.replace(/_/g, "-")}`} />
          ))}
        </Row>
        <div className="mt-4 grid md:grid-cols-3 gap-4">
          <TextInput label="Total (auto)" value={bradenTotal} onChange={() => {}} testid="nsa-braden-total" />
          <TextInput label="Risk category (auto)" value={bradenRisk(bradenTotal)} onChange={() => {}} testid="nsa-braden-risk" />
          <TextInput label="Notes" value={a.braden?.notes}
            onChange={(v) => setPath("braden", { notes: v })} testid="nsa-braden-notes" />
        </div>
      </SectionWrap>

      {/* --- Morse Fall Scale ---------------------------------------------- */}
      <SectionWrap eyebrow="Step 7 · Fall Risk" title="Morse Fall Scale"
        description="Sum of 6 items. ≥ 45 = High risk. Document interventions.">
        <Row>
          {Object.entries(MORSE_OPTIONS).map(([k, opts]) => (
            <SelectInput key={k}
              label={k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
              value={a.morse?.[k]} onChange={(v) => setPath("morse", { [k]: v })}
              options={opts} testid={`nsa-morse-${k.replace(/_/g, "-")}`} />
          ))}
        </Row>
        <div className="mt-4 grid md:grid-cols-3 gap-4">
          <TextInput label="Total (auto)" value={morseTotal} onChange={() => {}} testid="nsa-morse-total" />
          <TextInput label="Risk (auto)" value={morseRisk(morseTotal)} onChange={() => {}} testid="nsa-morse-risk" />
        </div>
        <div className="mt-3">
          <NarrativeField label="Fall risk interventions" testid="nsa-morse-interv"
            fieldKey="assessment.morse.interventions"
            fieldPurpose="Specific PCW interventions to mitigate fall risk: DME in place, bed-to-chair transfers, lighting, ambulation support, PRN assist."
            value={a.morse?.interventions} onChange={(v) => setPath("morse", { interventions: v })} />
        </div>
      </SectionWrap>

      {/* --- Skin Integrity ------------------------------------------------- */}
      <SectionWrap eyebrow="Step 7 · Skin" title="Skin Integrity">
        <Row>
          <SelectInput label="Skin intact?" value={a.skin?.intact}
            onChange={(v) => setPath("skin", { intact: v })} options={YN} testid="nsa-skin-intact" />
          <SelectInput label="Turgor" value={a.skin?.turgor}
            onChange={(v) => setPath("skin", { turgor: v })}
            options={SKIN_TURGOR} testid="nsa-skin-turgor" />
          <SelectInput label="Color" value={a.skin?.color}
            onChange={(v) => setPath("skin", { color: v })}
            options={SKIN_COLORS} testid="nsa-skin-color" />
        </Row>
        <div className="mt-3 grid md:grid-cols-2 gap-4">
          <TextInput label="Breakdown sites / wounds (site · stage)"
            placeholder="e.g. Sacrum Stage II 2x3cm"
            value={a.skin?.breakdown_sites}
            onChange={(v) => setPath("skin", { breakdown_sites: v })} testid="nsa-skin-breakdown" />
          <NarrativeField label="Skin notes" testid="nsa-skin-notes"
            fieldKey="assessment.skin.notes"
            fieldPurpose="Skin integrity observations, dressings, treatments, and prevention plan."
            value={a.skin?.notes} onChange={(v) => setPath("skin", { notes: v })} />
        </div>
      </SectionWrap>

      {/* --- Continence ---------------------------------------------------- */}
      {/* --- Continence Baseline ------------------------------------------ */}
      <SectionWrap eyebrow="Step 7 · Continence" title="Continence Baseline">
        <Row>
          <SelectInput label="Bladder pattern" value={a.continence?.bladder_pattern}
            onChange={(v) => setPath("continence", { bladder_pattern: v })}
            options={URINARY_PATTERNS}
            testid="nsa-cont-bladder" />
          <SelectInput label="Bowel pattern" value={a.continence?.bowel_pattern}
            onChange={(v) => setPath("continence", { bowel_pattern: v })}
            options={BOWEL_PATTERNS}
            testid="nsa-cont-bowel" />
          <TextInput label="Last bowel movement" type="date" value={a.continence?.last_bm}
            onChange={(v) => setPath("continence", { last_bm: v })} testid="nsa-cont-last-bm" />
          <TextInput label="Bladder / voiding schedule" value={a.continence?.bladder_schedule}
            onChange={(v) => setPath("continence", { bladder_schedule: v })} testid="nsa-cont-schedule" />
          <TextInput label="Bowel program (if any)" value={a.continence?.bowel_program}
            onChange={(v) => setPath("continence", { bowel_program: v })} testid="nsa-cont-program" />
        </Row>
      </SectionWrap>

      {/* --- Continence Detail (freq, briefs, supports) ------------------- */}
      <ContinenceDetailSection />

      {/* --- Sensory: Nose / Pharynx / Mouth ------------------------------ */}
      <SensorySection />

      {/* --- Lifestyle: Smoking / Alcohol --------------------------------- */}
      <LifestyleSection />

      {/* --- Weekly cognitive / behavioral symptoms ----------------------- */}
      <WeeklyBehaviorsSection />

      {/* --- IADL detail: meals / feeding / shopping ---------------------- */}
      <IadlDetailSection />

      {/* --- Transportation ----------------------------------------------- */}
      <TransportationSection />

      {/* --- Shopping / Housekeeping capability + tasks ------------------- */}
      <HousekeepingSection />

      {/* --- Activities permitted & favorites ----------------------------- */}
      <ActivitiesSection />

      {/* --- Medication management detail --------------------------------- */}
      <MedicationManagementSection />

      {/* --- Advance Directives -------------------------------------------- */}
      <SectionWrap eyebrow="Step 7 · Directives" title="Advance Directives">
        <Row>
          <SelectInput label="Living will" value={a.directives?.living_will}
            onChange={(v) => setPath("directives", { living_will: v })}
            options={YNU} testid="nsa-dir-living-will" />
          {a.directives?.living_will === "Yes" && (
            <>
              <SelectInput label="Living will — copy provided to agency" value={a.directives?.living_will_copy_provided}
                onChange={(v) => setPath("directives", { living_will_copy_provided: v })}
                options={YN} testid="nsa-dir-lw-copy-provided" />
              <SelectInput label="Living will — copy requested" value={a.directives?.living_will_copy_requested}
                onChange={(v) => setPath("directives", { living_will_copy_requested: v })}
                options={YN} testid="nsa-dir-lw-copy-requested" />
            </>
          )}
          <SelectInput label="DNR" value={a.directives?.dnr}
            onChange={(v) => setPath("directives", { dnr: v })}
            options={YNU} testid="nsa-dir-dnr" />
          {a.directives?.dnr === "Yes" && (
            <SelectInput label="DNR — copy provided" value={a.directives?.dnr_copy_provided}
              onChange={(v) => setPath("directives", { dnr_copy_provided: v })}
              options={YN} testid="nsa-dir-dnr-copy" />
          )}
          <SelectInput label="POLST" value={a.directives?.polst}
            onChange={(v) => setPath("directives", { polst: v })}
            options={YNU} testid="nsa-dir-polst" />
          {a.directives?.polst === "Yes" && (
            <SelectInput label="POLST — copy provided" value={a.directives?.polst_copy_provided}
              onChange={(v) => setPath("directives", { polst_copy_provided: v })}
              options={YN} testid="nsa-dir-polst-copy" />
          )}
          <SelectInput label="Healthcare POA" value={a.directives?.healthcare_poa}
            onChange={(v) => setPath("directives", { healthcare_poa: v })}
            options={YNU} testid="nsa-dir-poa" />
          <TextInput label="POA / HCPOA name" value={a.directives?.poa_name}
            onChange={(v) => setPath("directives", { poa_name: v })} testid="nsa-dir-poa-name" />
          <TextInput label="POA phone" value={a.directives?.poa_phone}
            onChange={(v) => setPath("directives", { poa_phone: v })} testid="nsa-dir-poa-phone" />
          <TextInput label="Where documents are kept" value={a.directives?.location_of_documents}
            onChange={(v) => setPath("directives", { location_of_documents: v })} testid="nsa-dir-loc" />
        </Row>
      </SectionWrap>

      {/* --- Caregiver Capability ----------------------------------------- */}
      <CaregiverCapabilitySection />

      {/* --- Caregiver Burden (Zarit-lite) --------------------------------- */}
      <SectionWrap eyebrow="Step 7 · Caregiver" title="Primary Caregiver & Burden"
        description="Short 5-item Zarit-derived scan. Total 0–20. ≥ 10 suggests respite referral.">
        <Row>
          <TextInput label="Primary caregiver name" value={a.caregiver?.primary_name}
            onChange={(v) => setPath("caregiver", { primary_name: v })} testid="nsa-cg-name" />
          <TextInput label="Relationship" value={a.caregiver?.primary_relationship}
            onChange={(v) => setPath("caregiver", { primary_relationship: v })} testid="nsa-cg-rel" />
          <TextInput label="Phone" value={a.caregiver?.primary_phone}
            onChange={(v) => setPath("caregiver", { primary_phone: v })} testid="nsa-cg-phone" />
          <TextInput label="Unpaid caregiving hours/week" value={a.caregiver?.hours_per_week}
            onChange={(v) => setPath("caregiver", { hours_per_week: v })} testid="nsa-cg-hrs" />
        </Row>
        <div className="mt-4 space-y-3">
          <FieldLabel>Caregiver burden screen (0-4 per item)</FieldLabel>
          <Row>
            <SelectInput label="Feel you are asked to do too much?" value={a.caregiver?.z1_too_much}
              onChange={(v) => setPath("caregiver", { z1_too_much: v })} options={SCALE_0_4} testid="nsa-cg-z1" />
            <SelectInput label="Not enough time for yourself?" value={a.caregiver?.z2_no_time_self}
              onChange={(v) => setPath("caregiver", { z2_no_time_self: v })} options={SCALE_0_4} testid="nsa-cg-z2" />
            <SelectInput label="Stressed balancing other responsibilities?" value={a.caregiver?.z3_stressed_combining}
              onChange={(v) => setPath("caregiver", { z3_stressed_combining: v })} options={SCALE_0_4} testid="nsa-cg-z3" />
            <SelectInput label="Relationship strained by caregiving?" value={a.caregiver?.z4_strained_caregiving}
              onChange={(v) => setPath("caregiver", { z4_strained_caregiving: v })} options={SCALE_0_4} testid="nsa-cg-z4" />
            <SelectInput label="Overall burden feels high?" value={a.caregiver?.z5_burden}
              onChange={(v) => setPath("caregiver", { z5_burden: v })} options={SCALE_0_4} testid="nsa-cg-z5" />
            <TextInput label="Total (auto)" value={zaritTotal} onChange={() => {}} testid="nsa-cg-total" />
            <SelectInput label="Respite / PCW relief requested?" value={a.caregiver?.respite_requested}
              onChange={(v) => setPath("caregiver", { respite_requested: v })} options={YN} testid="nsa-cg-respite" />
          </Row>
        </div>
      </SectionWrap>

      {/* --- Emergency Preparedness ---------------------------------------- */}
      <SectionWrap eyebrow="Step 7 · Emergency" title="Emergency Preparedness"
        description="Required for CMS Conditions of Participation and DHS 105.17 safety review.">
        <Row>
          <SelectInput label="Written evacuation plan?" value={a.emergency?.evacuation_plan}
            onChange={(v) => setPath("emergency", { evacuation_plan: v })} options={YN} testid="nsa-em-evac" />
          <TextInput label="Evacuation assistance needed" value={a.emergency?.evacuation_assistance_needed}
            onChange={(v) => setPath("emergency", { evacuation_assistance_needed: v })} testid="nsa-em-assist" />
          <SelectInput label="Oxygen in home?" value={a.emergency?.oxygen_in_home}
            onChange={(v) => setPath("emergency", { oxygen_in_home: v })} options={YN} testid="nsa-em-ox" />
          <SelectInput label="Smoke detectors working?" value={a.emergency?.smoke_detectors}
            onChange={(v) => setPath("emergency", { smoke_detectors: v })} options={YN} testid="nsa-em-smoke" />
          <TextInput label="Power-dependent equipment (comma-separated)"
            placeholder="O₂ concentrator, CPAP, feeding pump, hospital bed"
            value={(a.emergency?.power_dependent_equipment || []).join(", ")}
            onChange={(v) => setPath("emergency", { power_dependent_equipment: v.split(",").map((s) => s.trim()).filter(Boolean) })}
            testid="nsa-em-power" />
          <TextInput label="Backup power plan" value={a.emergency?.backup_power_plan}
            onChange={(v) => setPath("emergency", { backup_power_plan: v })} testid="nsa-em-backup" />
          <TextInput label="Fire hazard notes" value={a.emergency?.fire_hazard_notes}
            onChange={(v) => setPath("emergency", { fire_hazard_notes: v })} testid="nsa-em-fire" />
          <TextInput label="Nearest ER" value={a.emergency?.nearest_er}
            onChange={(v) => setPath("emergency", { nearest_er: v })} testid="nsa-em-er" />
          <TextInput label="Emergency contact chain" value={a.emergency?.emergency_contact_chain}
            onChange={(v) => setPath("emergency", { emergency_contact_chain: v })} testid="nsa-em-contacts" />
        </Row>
      </SectionWrap>

      {/* --- Oxygen Safety (branches on oxygen_in_home === Yes) ----------- */}
      <OxygenSafetySection />

      {/* --- Safety / Abuse / Neglect screening --------------------------- */}
      <SafetyAbuseSection />

      {/* --- Financial ---------------------------------------------------- */}
      <FinancialSection />

      {/* --- Teaching materials provided ---------------------------------- */}
      <TeachingSection />

      {/* --- MAHC-10 Fall Risk -------------------------------------------- */}
      <MAHC10Section />

      {/* --- GG-style functional scores ----------------------------------- */}
      <GGScoresSection />

      {/* --- Prior functioning + Multi-factor falls ----------------------- */}
      <PriorFunctioningSection />

      {/* --- Mental / Mood ------------------------------------------------- */}
      <SectionWrap eyebrow="Step 7 · Mood" title="Mood / Mental Status Snapshot"
        description="Complements Step 4 cognition. PHQ-2 is a 2-item depression screen (0–6).">
        <Row>
          <SelectInput label="Mood" value={a.mood?.mood}
            onChange={(v) => setPath("mood", { mood: v })}
            options={MOOD_TYPES} testid="nsa-mood-mood" />
          <TextInput label="Affect" value={a.mood?.affect}
            onChange={(v) => setPath("mood", { affect: v })} testid="nsa-mood-affect" />
          <SelectInput label="PHQ-2 (0-6)" value={a.mood?.depression_screen_phq2}
            onChange={(v) => setPath("mood", { depression_screen_phq2: v })}
            options={Array.from({ length: 7 }, (_, i) => ({ value: String(i), label: `${i}` }))}
            testid="nsa-mood-phq2" />
          <SelectInput label="Safety / SI" value={a.mood?.safety_ideation}
            onChange={(v) => setPath("mood", { safety_ideation: v })}
            options={SUICIDAL_LEVELS} testid="nsa-mood-si" />
        </Row>
        <div className="mt-3 text-[12px] text-[#3A5275]">{PHQ2_NOTE}</div>
        <div className="mt-3">
          <NarrativeField label="Mental / emotional notes" testid="nsa-mood-notes"
            fieldKey="assessment.mood.notes"
            fieldPurpose="Mental status observations, safety concerns, referral triggers, coping strategies."
            value={a.mood?.notes} onChange={(v) => setPath("mood", { notes: v })} />
        </div>
      </SectionWrap>

      {/* --- Summary narrative --------------------------------------------- */}
      <SectionWrap eyebrow="Step 7 · Summary" title="RN Summary Impression"
        description="Brief 2-3 sentence clinical gestalt — feeds the AI review and the NSA PDF.">
        <NarrativeField label="Assessment summary" testid="nsa-summary"
          fieldKey="assessment.summary_narrative"
          fieldPurpose="2-3 sentence RN clinical gestalt — current functional baseline, biggest risks, and what the PCW plan protects against."
          value={a.summary_narrative} onChange={(v) => update("assessment", { summary_narrative: v })} />
      </SectionWrap>

      {/* --- General notes + patient / rep signature ---------------------- */}
      <NotesAndSignatureSection />
    </div>
  );
}

// Expose computed helpers so the PDF generator and AI reviewer can recompute.
export { bradenRisk, morseRisk };
